/**
 * HTTP Client for Checkpoint API
 * 
 * Handles all HTTP communication with the Checkpoint server,
 * including automatic token management and request/response handling.
 * 
 * @package @appfactory/checkpoint-client
 * @version 1.0.0
 */

import {
  CheckpointConfig,
  ApiResponse,
  CheckpointError,
  TokenPair,
  TokenStorage,
  StorageType,
} from './types';

/**
 * Storage keys for tokens
 */
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'checkpoint_access_token',
  REFRESH_TOKEN: 'checkpoint_refresh_token',
  TOKEN_EXPIRY: 'checkpoint_token_expiry',
} as const;

/**
 * In-memory storage implementation
 */
class MemoryStorage implements TokenStorage {
  private store = new Map<string, string>();

  getItem(key: string): string | null {
    return this.store.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value);
  }

  removeItem(key: string): void {
    this.store.delete(key);
  }
}

/**
 * HTTP Client for Checkpoint API
 */
export class HttpClient {
  private config: Required<Pick<CheckpointConfig, 'baseUrl' | 'timeout'>> & CheckpointConfig;
  private storage: TokenStorage;
  private refreshPromise: Promise<TokenPair> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(config: CheckpointConfig) {
    this.config = {
      ...config,
      timeout: config.timeout ?? 30000,
      autoRefresh: config.autoRefresh ?? true,
      refreshThreshold: config.refreshThreshold ?? 300, // 5 minutes
    };

    // Setup storage
    this.storage = this.initStorage(config.storage, config.customStorage);

    // Start auto-refresh if enabled
    if (this.config.autoRefresh) {
      this.startAutoRefresh();
    }
  }

  /**
   * Initialize token storage
   */
  private initStorage(type?: StorageType, custom?: TokenStorage): TokenStorage {
    if (custom) {
      return custom;
    }

    if (typeof window === 'undefined') {
      return new MemoryStorage();
    }

    switch (type) {
      case 'sessionStorage':
        return window.sessionStorage;
      case 'memory':
        return new MemoryStorage();
      case 'localStorage':
      default:
        return window.localStorage;
    }
  }

  /**
   * Make an HTTP request to the API
   */
  async request<T>(
    method: string,
    path: string,
    data?: unknown,
    requireAuth = false
  ): Promise<T> {
    const url = `${this.config.baseUrl}/checkpoint${path}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.config.headers,
    };

    // Add authorization header if we have a token
    const skipRefreshPaths = new Set([
      '/auth/token/refresh',
      '/auth/logout',
      '/auth/login',
      '/auth/login/username',
      '/auth/register',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/verify-email',
      '/auth/sso/login',
      '/auth/sso/exchange',
    ]);
    const skipRefresh = skipRefreshPaths.has(path);
    const accessToken = await this.getAccessToken(!skipRefresh);
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    } else if (requireAuth) {
      throw new CheckpointError('Authentication required', 'AUTH_REQUIRED', 401);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: data ? JSON.stringify(data) : null,
        signal: controller.signal,
        credentials: 'include',
      });

      clearTimeout(timeoutId);

      const json: ApiResponse<T> = await response.json();

      if (!json.success || json.error) {
        throw new CheckpointError(
          json.error?.message ?? 'Request failed',
          json.error?.code ?? 'UNKNOWN_ERROR',
          response.status
        );
      }

      return json.data;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof CheckpointError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new CheckpointError('Request timeout', 'TIMEOUT');
        }
        throw new CheckpointError(error.message, 'NETWORK_ERROR');
      }

      throw new CheckpointError('Unknown error', 'UNKNOWN_ERROR');
    }
  }

  /**
   * GET request
   */
  get<T>(path: string, requireAuth = false): Promise<T> {
    return this.request<T>('GET', path, undefined, requireAuth);
  }

  /**
   * POST request
   */
  post<T>(path: string, data?: unknown, requireAuth = false): Promise<T> {
    return this.request<T>('POST', path, data, requireAuth);
  }

  /**
   * PUT request
   */
  put<T>(path: string, data?: unknown, requireAuth = false): Promise<T> {
    return this.request<T>('PUT', path, data, requireAuth);
  }

  /**
   * DELETE request
   */
  delete<T>(path: string, requireAuth = false): Promise<T> {
    return this.request<T>('DELETE', path, undefined, requireAuth);
  }

  /**
   * Store tokens
   */
  async setTokens(tokens: TokenPair): Promise<void> {
    const expiry = Date.now() + tokens.expires_in * 1000;

    await this.storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
    await this.storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
    await this.storage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiry.toString());

    if (this.config.onTokenRefresh) {
      this.config.onTokenRefresh(tokens);
    }

    // Restart auto-refresh timer
    if (this.config.autoRefresh) {
      this.startAutoRefresh();
    }
  }

  /**
   * Clear stored tokens
   */
  async clearTokens(): Promise<void> {
    await this.storage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    await this.storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    await this.storage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Get the current access token
   */
  async getAccessToken(allowRefresh = true): Promise<string | null> {
    const token = await this.storage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const expiryStr = await this.storage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);

    if (!token || !expiryStr) {
      return null;
    }

    const expiry = parseInt(expiryStr, 10);
    const now = Date.now();

    // Token expired - try to refresh
    if (now >= expiry) {
      if (!allowRefresh) {
        return null;
      }
      try {
        const newTokens = await this.refreshTokens();
        return newTokens.access_token;
      } catch {
        await this.clearTokens();
        return null;
      }
    }

    return token;
  }

  /**
   * Get the current refresh token
   */
  async getRefreshToken(): Promise<string | null> {
    return this.storage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Check if authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const token = await this.getAccessToken();
    return token !== null;
  }

  /**
   * Refresh tokens using the refresh token
   */
  async refreshTokens(): Promise<TokenPair> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = await this.storage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) {
      throw new CheckpointError('No refresh token available', 'NO_REFRESH_TOKEN', 401);
    }

    this.refreshPromise = this.post<TokenPair>('/auth/token/refresh', {
      refresh_token: refreshToken,
    })
      .then(async (tokens) => {
        await this.setTokens(tokens);
        return tokens;
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  /**
   * Start auto-refresh timer
   */
  private async startAutoRefresh(): Promise<void> {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const expiryStr = await this.storage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    if (!expiryStr) {
      return;
    }

    const expiry = parseInt(expiryStr, 10);
    const now = Date.now();
    const threshold = (this.config.refreshThreshold ?? 300) * 1000;
    const refreshAt = expiry - threshold;

    if (refreshAt <= now) {
      // Token is about to expire or already expired
      try {
        await this.refreshTokens();
      } catch {
        // Refresh failed, tokens will be cleared on next request
      }
      return;
    }

    // Schedule refresh
    const delay = refreshAt - now;
    this.refreshTimer = setTimeout(async () => {
      try {
        await this.refreshTokens();
      } catch {
        // Refresh failed
      }
    }, delay);
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
